/**
 * Recognition page — company-wide kudos feed.
 *
 * Layout:
 *   - Header: "Recognition" + the requester's own karma + level + balance pill.
 *   - Feed: cursor-paginated list of `kudos-given` events (newest first),
 *     prepended in real time via `useRecognitionSocket`.
 *
 * On a phone the feed flows in the page and the page scrolls; from `md` the feed has its own scrolling box.
 *
 * Initial fetch happens on mount + on companyId change. Socket listener for
 * the company room keeps the feed warm without polling; we still depend on
 * the per-page subscribe for resilience to socket dropouts (the panel layer
 * already handles reconnect refresh).
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { ChevronDown } from "lucide-react";
import { Spinner, Page, Card, TextField, Select } from "@shopify/polaris";
import { AppDispatch } from "configureStore";
import { useAppSelector } from "hooks";
import UserAvatar from "components/user-avatar/user-avatar";
import useIsPhone from "features/files/use-is-phone";
import {
  setModalxComponent,
  setModalxComponentProps,
  setIsModalxOpen,
} from "components/modalx/modalx.slice";
import {
  fetchSnapshotThunk,
  fetchFeedThunk,
  fetchMoreFeedThunk,
  markRecognitionRead,
} from "./recognition.slice";
import LevelBadge from "./level-badge";

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

const Recognition: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const { companyId } = useParams<{ companyId: string }>();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);

  const {
    snapshot,
    snapshotCompanyId,
    feed,
    feedCompanyId,
    feedHasMore,
    feedNextCursor,
    loadingFeed,
    loadingMore,
  } = useAppSelector((state) => state.recognitionReducer);

  // Search & Filter State
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [type, setType] = useState<"all" | "sent" | "received">("all");
  const [amount, setAmount] = useState<string>("all");
  // On a phone the type and amount filters sit behind a button; the search stays in view.
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const filterBoxClass = areFiltersOpen ? "min-w-[150px]" : "min-w-[150px] max-md:hidden";

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset filters when companyId changes
  useEffect(() => {
    setSearch("");
    setDebouncedSearch("");
    setType("all");
    setAmount("all");
  }, [companyId]);

  // Load snapshot on company change
  useEffect(() => {
    if (!companyId) return;
    if (snapshotCompanyId !== companyId) {
      dispatch(fetchSnapshotThunk(companyId));
    }
  }, [companyId, dispatch, snapshotCompanyId]);

  // Fetch feed on mount, company change, or filter change
  useEffect(() => {
    if (!companyId) return;
    dispatch(
      fetchFeedThunk({
        companyId,
        search: debouncedSearch.trim() || undefined,
        type: type !== "all" ? type : undefined,
        amount: amount !== "all" ? amount : undefined,
      })
    );
  }, [companyId, debouncedSearch, type, amount, dispatch]);

  // Clear the menu badge when the page is on screen. We re-fire on every
  // feed change so events that arrive while we're already on the page also
  // reset the count immediately (without waiting for the next mount).
  useEffect(() => {
    if (companyId) dispatch(markRecognitionRead(companyId));
  }, [companyId, dispatch, feedCompanyId, feed.length]);

  // Infinite scroll — IntersectionObserver on a sentinel at feed bottom. The root is the feed's own
  // scrolling box, or the page itself (null) on a phone where the feed has no box.
  useEffect(() => {
    if (!sentinelRef.current || (!isPhone && !scrollRoot) || !companyId) return;
    if (!feedHasMore || feedNextCursor == null) return;
    const el = sentinelRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          !loadingMore &&
          feedNextCursor != null
        ) {
          dispatch(
            fetchMoreFeedThunk({
              companyId,
              cursor: feedNextCursor,
              search: debouncedSearch.trim() || undefined,
              type: type !== "all" ? type : undefined,
              amount: amount !== "all" ? amount : undefined,
            })
          );
        }
      },
      {
        root: isPhone ? null : scrollRoot,
        rootMargin: "200px",
      }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [
    companyId,
    feedHasMore,
    feedNextCursor,
    loadingMore,
    dispatch,
    debouncedSearch,
    type,
    amount,
    scrollRoot,
    isPhone,
  ]);

  if (!companyId) return null;

  const mySnapshot = snapshotCompanyId === companyId ? snapshot : null;
  const myFeed = feedCompanyId === companyId ? feed : [];

  const handleAuthorClick = (userId: string) => {
    dispatch(setModalxComponent("UserPublicProfileModal"));
    dispatch(setModalxComponentProps({ companyId, userId }));
    dispatch(setIsModalxOpen(true));
  };

  return (
    <Page fullWidth title={t("recognition", { defaultValue: "Recognition" })}>
      <div className="flex flex-col md:h-[calc(100dvh-10rem)] md:min-h-[320px] md:overflow-hidden">
        {mySnapshot && (
          <div className="mb-4 shrink-0">
            <Card>
              <div className="p-3 md:p-4 flex items-center gap-4 flex-wrap">
                <LevelBadge
                  level={mySnapshot.level}
                  showProgress
                  showThreshold={isPhone}
                />
                <div className="flex flex-col">
                  <span className="text-xs md:text-[11px] uppercase tracking-wide text-gray-500">
                    {t("karma_unit", { defaultValue: "karma" })}
                  </span>
                  <span className="text-lg font-semibold">
                    {mySnapshot.karma}
                  </span>
                </div>
                <div className="flex flex-col ml-2">
                  <span className="text-xs md:text-[11px] uppercase tracking-wide text-gray-500">
                    {t("balance", { defaultValue: "Balance" })}
                  </span>
                  <span
                    className={`text-lg font-semibold ${
                      mySnapshot.giveBalance > 0
                        ? "text-green-700"
                        : "text-gray-400"
                    }`}
                  >
                    ⚡ {mySnapshot.giveBalance}
                  </span>
                </div>
                <div className="basis-full text-xs text-gray-500 md:ml-auto md:basis-auto md:max-w-xs md:text-right">
                  {t("kudos_balance_tooltip", {
                    defaultValue: "Kudos to give today (resets every day)",
                  })}
                </div>
              </div>
            </Card>
          </div>
        )}

        <div className="shrink-0 pb-3">
          <div className="flex flex-wrap items-end gap-3 w-full bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex-1 min-w-[200px]">
              <TextField
                label={t("search", { defaultValue: "Search" })}
                value={search}
                onChange={(val) => setSearch(val)}
                placeholder={t("search_kudos_placeholder", {
                  defaultValue: "Search by employee name or reason...",
                })}
                autoComplete="off"
              />
            </div>
            {isPhone && (
              <button
                type="button"
                aria-expanded={areFiltersOpen}
                onClick={() => setAreFiltersOpen(!areFiltersOpen)}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700"
              >
                {t("filters")}
                <ChevronDown
                  className={areFiltersOpen ? "h-4 w-4 rotate-180" : "h-4 w-4"}
                />
              </button>
            )}
            <div className={filterBoxClass}>
              <Select
                label={t("type", { defaultValue: "Type" })}
                options={[
                  {
                    label: t("all_kudos", { defaultValue: "All Kudos" }),
                    value: "all",
                  },
                  {
                    label: t("sent_by_me", { defaultValue: "Sent by Me" }),
                    value: "sent",
                  },
                  {
                    label: t("received_by_me", {
                      defaultValue: "Received by Me",
                    }),
                    value: "received",
                  },
                ]}
                value={type}
                onChange={(val) => setType(val as any)}
              />
            </div>
            <div className={filterBoxClass}>
              <Select
                label={t("amount", { defaultValue: "Amount" })}
                options={[
                  {
                    label: t("any_amount", { defaultValue: "Any amount" }),
                    value: "all",
                  },
                  { label: "1 ⚡", value: "1" },
                  { label: "2 ⚡", value: "2" },
                  { label: "3 ⚡", value: "3" },
                  { label: "4 ⚡", value: "4" },
                  { label: "5 ⚡", value: "5" },
                ]}
                value={amount}
                onChange={(val) => setAmount(val)}
              />
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm md:flex md:min-h-0 md:flex-1 md:flex-col">
          <div
            ref={setScrollRoot}
            className="md:min-h-0 md:flex-1 md:overflow-y-auto"
          >
            {loadingFeed && myFeed.length === 0 ? (
              <div className="p-10 flex justify-center">
                <Spinner size="large" />
              </div>
            ) : myFeed.length === 0 ? (
              <div className="p-10 text-center text-gray-500">
                {debouncedSearch.trim() || type !== "all" || amount !== "all"
                  ? t("no_kudos_found", {
                      defaultValue: "No kudos match your filters.",
                    })
                  : t("no_kudos_yet", {
                      defaultValue:
                        "No kudos in this company yet — be the first!",
                    })}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {myFeed.map((tx) => (
                  <li key={tx.id} className="p-4 flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => handleAuthorClick(tx.fromUser.id)}
                      className="shrink-0"
                    >
                      <UserAvatar
                        companyId={companyId}
                        user={tx.fromUser}
                        size={40}
                        rounded="lg"
                      />
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-1 text-sm">
                        <button
                          type="button"
                          onClick={() => handleAuthorClick(tx.fromUser.id)}
                          className="font-semibold text-gray-900 hover:underline"
                        >
                          {tx.fromUser.name}
                        </button>
                        <span className="text-gray-500">
                          {t("gave_kudos_to", {
                            defaultValue: "gave kudos to",
                          })}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleAuthorClick(tx.toUser.id)}
                          className="font-semibold text-gray-900 hover:underline"
                        >
                          {tx.toUser.name}
                        </button>
                        <span
                          className="tracking-tight"
                          title={`${tx.amount}`}
                          aria-label={`${tx.amount} kudos`}
                        >
                          {"⚡".repeat(tx.amount)}
                        </span>
                        <span className="ml-auto text-xs text-gray-400">
                          {formatTime(tx.createdAt)}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-700 whitespace-pre-wrap break-words">
                        {tx.reason}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div ref={sentinelRef} className="h-4 w-full" />
            {loadingMore && (
              <div className="p-4 flex justify-center">
                <Spinner size="small" />
              </div>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
};

export default Recognition;
